import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { eventsService } from '../../services/eventsService';
import { authService } from '../../services/api';
import { getApiUserCode } from '../Shared/ApiUserBadge';
import HeaderNewTabButton from '../Shared/HeaderNewTabButton';
import './FinancialsPage.css';

const TAB_LABELS = {
  New_York: 'NY',
  North_America: 'NA',
  World: 'World',
  Greece: 'Greece',
  London: 'London',
  Season_Tickets: 'Season Tickets',
};

const TAB_CURRENCIES = { New_York: 'USD', North_America: 'USD', World: 'EUR', Greece: 'EUR', London: 'GBP', Season_Tickets: 'USD' };
const addAmount = (left, right) => left == null || right == null ? null : Number(left) + Number(right);
const sumAmount = (rows, field) => rows.reduce((total, row) => addAmount(total, row[field]), 0);
const summaryIssues = (row) => [...new Set([
  ...(row?.currency_issues || []), ...(row?.tabs || []).flatMap((tab) => tab.currency_issues || []),
])];

const formatAmount = (value) => {
  const parsed = Number(value || 0);
  if (!Number.isFinite(parsed) || parsed === 0) return '-';
  const formatted = Math.abs(parsed).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return parsed < 0 ? `(${formatted})` : formatted;
};

const formatPercent = (value) => {
  if (value == null) return '-';
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '-';
  return `${parsed.toFixed(0)}%`;
};

const getToneClass = (value) => {
  const parsed = Number(value || 0);
  if (!Number.isFinite(parsed) || parsed === 0) return 'financials-neutral';
  return parsed > 0 ? 'financials-positive' : 'financials-negative';
};

const getRoiClass = (value) => {
  const parsed = Number(value || 0);
  if (!Number.isFinite(parsed) || parsed === 0) return 'financials-roi-neutral';
  return parsed > 0 ? 'financials-roi-positive' : 'financials-roi-negative';
};

const getTabSummary = (row, tab) => (
  (row?.tabs || []).find((item) => item.source_tab === tab) || {
    source_tab: tab,
    profit_loss: 0,
    revenue: 0,
    investment: 0,
    roi: null,
  }
);

const buildTotalsFromMonths = (months, tabs, label = 'YTD') => {
  const tabTotals = tabs.map((tab) => {
    const total = months.reduce((acc, month) => {
      const values = getTabSummary(month, tab);
      return {
        source_tab: tab,
        profit_loss: addAmount(acc.profit_loss, values.profit_loss),
        revenue: addAmount(acc.revenue, values.revenue),
        investment: addAmount(acc.investment, values.investment),
        unit_count: acc.unit_count + Number(values.unit_count || 0),
        sold_unit_count: acc.sold_unit_count + Number(values.sold_unit_count || 0),
      };
    }, {
      source_tab: tab,
      profit_loss: 0,
      revenue: 0,
      investment: 0,
      unit_count: 0,
      sold_unit_count: 0,
    });

    return {
      ...total,
      currency: TAB_CURRENCIES[tab] || 'USD',
      currency_issues: [...new Set(months.flatMap((month) => getTabSummary(month, tab).currency_issues || []))],
      roi: total.profit_loss != null && total.investment > 0 ? (total.profit_loss / total.investment) * 100 : null,
    };
  });

  const profitLoss = sumAmount(months, 'profit_loss');
  const revenue = sumAmount(months, 'revenue');
  const investment = sumAmount(months, 'investment');

  return {
    month_number: 0,
    month_label: label,
    currency: 'USD',
    currency_issues: [...new Set(months.flatMap((month) => month.currency_issues || []))],
    profit_loss: profitLoss,
    revenue,
    investment,
    roi: profitLoss != null && investment > 0 ? (profitLoss / investment) * 100 : null,
    unit_count: tabTotals.reduce((total, tab) => total + tab.unit_count, 0),
    sold_unit_count: tabTotals.reduce((total, tab) => total + tab.sold_unit_count, 0),
    tabs: tabTotals,
  };
};

const FinancialsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [year, setYear] = useState(new Date().getFullYear());
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currencyDetails, setCurrencyDetails] = useState(null);
  const [ytdOnly, setYtdOnly] = useState(false);
  const [currentApiUserCode, setCurrentApiUserCode] = useState('');

  const tabs = useMemo(() => summary?.tabs || [], [summary]);
  const routePrefix = location.pathname.startsWith('/dashboard') ? '/dashboard' : '/user';
  const canOpenEventsPnl = routePrefix === '/dashboard' || currentApiUserCode === 'TS';
  const currentDate = useMemo(() => new Date(), []);
  const currentYear = currentDate.getFullYear();
  const currentMonthNumber = currentDate.getMonth() + 1;
  const visibleMonths = useMemo(() => {
    const months = summary?.months || [];
    if (!ytdOnly) return months;
    return months.filter((month) => Number(month.month_number) <= currentMonthNumber);
  }, [currentMonthNumber, summary, ytdOnly]);
  const visibleTotals = useMemo(() => {
    if (!summary) return null;
    return ytdOnly ? buildTotalsFromMonths(visibleMonths, tabs) : summary.totals;
  }, [summary, tabs, visibleMonths, ytdOnly]);

  const loadFinancials = useCallback(async (targetYear) => {
    setLoading(true);
    setError('');
    const result = await eventsService.getFinancials({ year: targetYear });
    if (result.success) {
      setSummary(result);
      if (result.year && result.year !== targetYear) {
        setYear(result.year);
      }
    } else {
      setError(result.error);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadFinancials(year);
  }, [loadFinancials, year]);

  useEffect(() => {
    if (routePrefix === '/dashboard') {
      return;
    }

    const token = authService.getToken();
    if (!token) {
      setCurrentApiUserCode('');
      return;
    }

    let isMounted = true;
    authService.getCurrentUser(token)
      .then((user) => {
        if (isMounted) {
          setCurrentApiUserCode(getApiUserCode(user.email));
        }
      })
      .catch(() => {
        if (isMounted) {
          setCurrentApiUserCode('');
        }
      });

    return () => {
      isMounted = false;
    };
  }, [routePrefix]);

  const openEventsPnl = ({ monthLabel, sourceTab } = {}) => {
    if (!canOpenEventsPnl) {
      return;
    }

    const params = new URLSearchParams({ view: 'pnl' });
    if (monthLabel && monthLabel !== 'Total') {
      params.set('month', monthLabel);
    }
    if (sourceTab) {
      params.set('tab', sourceTab);
    }
    navigate(`${routePrefix}/events?${params.toString()}`);
  };

  const handleYearChange = (delta) => {
    setYtdOnly(false);
    setYear((current) => current + delta);
  };

  const handleYtdClick = () => {
    setYtdOnly(true);
    setYear(currentYear);
  };

  const renderValueButton = (row, sourceTab, field, className = '') => {
    const values = sourceTab ? getTabSummary(row, sourceTab) : row;
    const displayValue = formatAmount(values?.[field]);

    if (!canOpenEventsPnl) {
      return (
        <span className={`financials-cell-value ${className}`.trim()}>
          {displayValue}
        </span>
      );
    }

    return (
      <button
        type="button"
        className={`financials-cell-button ${className}`.trim()}
        onClick={() => openEventsPnl({ monthLabel: row.month_label, sourceTab })}
      >
        {displayValue}
      </button>
    );
  };

  const renderRows = (rows) => rows.map((row) => (
    <tr key={row.month_label}>
      <th className="financials-month-cell">
        {canOpenEventsPnl ? (
          <button type="button" onClick={() => openEventsPnl({ monthLabel: row.month_label })}>
            {row.month_label}
          </button>
        ) : (
          <span className="financials-month-label">{row.month_label}</span>
        )}
        {summaryIssues(row).length > 0 && (
          <button type="button" className="financials-currency-warning" aria-label={`Currency details for ${row.month_label}`}
            title="Currency assumptions, provisional rates, or missing information"
            onClick={() => setCurrencyDetails({ month: row.month_label, issues: summaryIssues(row) })}>!</button>
        )}
      </th>
      <td className={getToneClass(row.profit_loss)}>
        {renderValueButton(row, null, 'profit_loss')}
      </td>
      {tabs.map((tab) => {
        const tabSummary = getTabSummary(row, tab);
        return (
          <td key={`${row.month_label}-${tab}-profit`} className={getToneClass(tabSummary.profit_loss)}>
            {renderValueButton(row, tab, 'profit_loss')}
          </td>
        );
      })}
      {tabs.map((tab) => (
        <td key={`${row.month_label}-${tab}-revenue`}>
          {renderValueButton(row, tab, 'revenue')}
        </td>
      ))}
      <td>{renderValueButton(row, null, 'revenue', 'financials-total-button')}</td>
      {tabs.map((tab) => (
        <td key={`${row.month_label}-${tab}-investment`}>
          {renderValueButton(row, tab, 'investment')}
        </td>
      ))}
      <td>{renderValueButton(row, null, 'investment', 'financials-total-button')}</td>
      <td className={getRoiClass(row.roi)}>
        {canOpenEventsPnl ? (
          <button type="button" className="financials-cell-button" onClick={() => openEventsPnl({ monthLabel: row.month_label })}>
            {formatPercent(row.roi)}
          </button>
        ) : (
          <span className="financials-cell-value">{formatPercent(row.roi)}</span>
        )}
      </td>
    </tr>
  ));

  return (
    <div className="financials-page">
      <header className="financials-header">
        <div className="financials-header-content">
          <HeaderNewTabButton />
          <div>
            <h1>Financials</h1>
          </div>
          <div className="financials-header-actions">
            {canOpenEventsPnl && (
              <button className="btn btn-primary" type="button" onClick={() => navigate(`${routePrefix}/financials/reports`)}>
                Reports
              </button>
            )}
            <button className="btn btn-secondary" type="button" onClick={() => navigate(routePrefix)}>
              Back to Dashboard
            </button>
          </div>
        </div>
      </header>

      <main className="financials-main">
        <div className="financials-toolbar">
          <div className="financials-year-controls">
            <button type="button" className="btn btn-secondary" onClick={() => handleYearChange(-1)}>
              Previous
            </button>
            <div className="financials-year">{year}</div>
            <button type="button" className="btn btn-secondary" onClick={() => handleYearChange(1)}>
              Next
            </button>
          </div>
          <button
            type="button"
            className={`btn ${ytdOnly ? 'btn-primary' : 'btn-secondary'} financials-ytd-btn`}
            onClick={handleYtdClick}
          >
            YTD
          </button>
        </div>

        {error && <div className="notification error">{error}</div>}
        {currencyDetails && (
          <section className="financials-currency-details" role="dialog" aria-label="Currency details">
            <h2>{currencyDetails.month} — Currency details</h2>
            <button type="button" className="btn btn-secondary" onClick={() => setCurrencyDetails(null)}>Close</button>
            <ul>{currencyDetails.issues.map((issue) => <li key={issue}>{issue}</li>)}</ul>
          </section>
        )}

        {loading ? (
          <div className="financials-loading">
            <div className="loading-spinner"></div>
            <p>Loading financials...</p>
          </div>
        ) : (
          <div className="financials-table-shell">
            <table className="financials-table">
              <thead>
                <tr>
                  <th rowSpan="2" className="financials-year-header">{summary?.year || year}</th>
                  <th rowSpan="2" className="financials-total-header">Total P/L (USD)</th>
                  <th colSpan={tabs.length}>Profits</th>
                  <th colSpan={tabs.length + 1}>Revenue</th>
                  <th colSpan={tabs.length + 1}>Investment</th>
                  <th rowSpan="2">ROI %</th>
                </tr>
                <tr>
                  {tabs.map((tab) => <th key={`profit-${tab}`}>{TAB_LABELS[tab] || tab} ({TAB_CURRENCIES[tab] || 'USD'})</th>)}
                  {tabs.map((tab) => <th key={`revenue-${tab}`}>{TAB_LABELS[tab] || tab} ({TAB_CURRENCIES[tab] || 'USD'})</th>)}
                  <th>Total (USD)</th>
                  {tabs.map((tab) => <th key={`investment-${tab}`}>{TAB_LABELS[tab] || tab} ({TAB_CURRENCIES[tab] || 'USD'})</th>)}
                  <th>Total (USD)</th>
                </tr>
              </thead>
              <tbody>
                {renderRows(visibleMonths)}
              </tbody>
              <tfoot>
                {visibleTotals ? renderRows([visibleTotals]) : null}
              </tfoot>
            </table>
          </div>
        )}
      </main>
    </div>
  );
};

export { buildTotalsFromMonths };
export default FinancialsPage;
